/**
 * The one place a thrown auth error becomes something a user may read.
 *
 * Everything the API throws arrives here as an English string, and everything
 * that leaves here is copy we wrote, pinned to the field it belongs to. **No
 * raw server string reaches a user**, because plenty of them are internal
 * ('Token Not Found', 'User Not Found', a yup message about a field the form
 * does not even have).
 *
 * WHY SUBSTRINGS AND NOT ERROR CODES: the backend installs no `formatError`, so
 * a yup validation failure arrives as a stock Apollo error with
 * `extensions.code === 'INTERNAL_SERVER_ERROR'` — indistinguishable from a
 * crash — and every deliberate `ApolloError` throw carries a literal HTTP
 * status STRING ('400', '404', '409') as its code. The message is the only
 * reliable signal, which is why master plan D-6 freezes the backend's auth
 * copy in `rules.constants.ts` and gates it on a drift check. `RATE_LIMITED`
 * is the single exception: it is a real, stable code from the rate-limit
 * middleware.
 *
 * BRANCH ORDER IS FIXED BY MASTER PLAN D-3 and it matters: network, Google,
 * token, throttle, then the generic substring branches LAST. The generic
 * branches match loosely ('username', 'password') and will swallow every
 * specific case above them if they run first.
 */
import type { AuthField } from './authValidation';

export type AuthErrorContext = 'login' | 'register' | 'google' | 'forgot' | 'reset';

export interface ParsedAuthError {
  field: AuthField | 'form';
  message: string;
  /** 'forgot' + "email not registered": render the neutral success card, not an error. */
  suppressed?: boolean;
  /** "Authentication Must Be Provided": let apollo.ts's errorLink own the redirect. */
  passthrough?: boolean;
}

const NETWORK_MESSAGE =
  "Can't reach the server. Check your connection and try again.";

const TOKEN_MESSAGE =
  'This reset link is invalid or has already been used. Request a new one.';

const THROTTLE_MESSAGE = 'Too many requests. Please wait a minute and try again.';

/** Last resort, per context. Deliberately says nothing about which half failed. */
const FALLBACK: Record<AuthErrorContext, string> = {
  login: 'Something went wrong. Please try again.',
  register: 'Could not create your account. Please try again.',
  google: 'Could not sign you in with Google. Please try again.',
  forgot: 'Could not send the reset link. Please try again.',
  reset: 'Could not reset your password. Please try again.',
};

/**
 * Pull the most useful raw message out of whatever Apollo or JS handed us, and
 * flag transport-level failures so the caller can say "offline" instead of
 * "wrong password".
 *
 * The nested lookup is the load-bearing part: a GraphQL server can return
 * structured errors *inside* a network error (a 400 or 500 carrying a JSON
 * body), and that body is the real message. Preferring it — and reporting
 * `isNetwork: false` when it exists — is what stops a validation failure from
 * being reported as a connectivity problem.
 */
export function rawMessage(err: unknown): { message: string; isNetwork: boolean } {
  const anyErr = err as {
    networkError?: { message?: string; result?: { errors?: Array<{ message?: string }> } };
    graphQLErrors?: Array<{ message?: string }>;
    message?: string;
  } | null;

  if (anyErr?.networkError) {
    const nested = anyErr.networkError.result?.errors?.[0]?.message;
    if (typeof nested === 'string' && nested) return { message: nested, isNetwork: false };
    return { message: anyErr.networkError.message || 'Network error', isNetwork: true };
  }

  const gql = anyErr?.graphQLErrors?.[0]?.message;
  if (typeof gql === 'string' && gql) return { message: gql, isNetwork: false };
  if (typeof anyErr?.message === 'string' && anyErr.message) {
    return { message: anyErr.message, isNetwork: false };
  }
  return { message: 'Something went wrong.', isNetwork: false };
}

type ErrorExtensions = { code?: unknown; retryAfter?: unknown };
type ErrorBody = { errors?: Array<{ extensions?: ErrorExtensions }> };

/**
 * One `extensions` value off the first GraphQL error, wherever it was put.
 *
 * Three shapes reach here, and a 429 does not look like the others:
 *  - a resolver throw: an ApolloError with `graphQLErrors[0].extensions`;
 *  - the rate-limit middleware's 429 (`promostork_backend/src/middleware/
 *    rateLimit.ts`): its body has `data: null`, so Apollo raises a plain
 *    ServerError and leaves `graphQLErrors` EMPTY. The only copy of the code
 *    is `networkError.result.errors[0].extensions` — or `result.errors[0]` on
 *    the bare ServerError a link sees before Apollo wraps it;
 *  - a single GraphQLError, with `extensions` on itself.
 *
 * Not auth-only: `errorCode`, `retryAfterSeconds` and `httpStatus` are also how
 * apollo.ts's rate-limit retry, the view-tracking queue and the data hooks in
 * useAppData.ts read a 429.
 */
function extensionValue(err: unknown, key: keyof ErrorExtensions): unknown {
  const anyErr = err as {
    graphQLErrors?: Array<{ extensions?: ErrorExtensions }>;
    networkError?: { result?: ErrorBody } | null;
    result?: ErrorBody;
    extensions?: ErrorExtensions;
  } | null;
  return (
    anyErr?.graphQLErrors?.[0]?.extensions?.[key] ??
    anyErr?.networkError?.result?.errors?.[0]?.extensions?.[key] ??
    anyErr?.result?.errors?.[0]?.extensions?.[key] ??
    anyErr?.extensions?.[key]
  );
}

/**
 * `extensions.retryAfter` (seconds) off the first GraphQL error.
 *
 * `checkResetThrottle` in `apps/promostork_backend/src/utils/resetThrottle.ts`
 * attaches the seconds left on its own window to the 429 it throws, and the
 * rate-limit middleware does the same in its 429 body. Preferring that number
 * over a client-side constant keeps a countdown or a retry correct if the
 * backend ever retunes the window. (The middleware's `Retry-After` header is
 * not CORS-exposed, so the body is the only place the browser can read it.)
 */
export function retryAfterSeconds(err: unknown): number | undefined {
  const value = extensionValue(err, 'retryAfter');
  return typeof value === 'number' && value > 0 ? value : undefined;
}

/** `extensions.code` off the first GraphQL error, if the throw carries one. */
export function errorCode(err: unknown): string | undefined {
  const code = extensionValue(err, 'code');
  return typeof code === 'string' ? code : undefined;
}

/**
 * HTTP status of a non-2xx response, off an ApolloError (`networkError`) or the
 * bare ServerError a link sees. `undefined` when no response arrived at all
 * (offline, DNS, CORS) or the error never touched the network.
 */
export function httpStatus(err: unknown): number | undefined {
  const anyErr = err as {
    networkError?: { statusCode?: unknown } | null;
    statusCode?: unknown;
  } | null;
  const status = anyErr?.networkError?.statusCode ?? anyErr?.statusCode;
  return typeof status === 'number' ? status : undefined;
}

export function parseAuthError(err: unknown, context: AuthErrorContext): ParsedAuthError {
  const { message, isNetwork } = rawMessage(err);
  const m = message.toLowerCase();

  // 1 ─ Network. Consume `isNetwork`; do not re-derive the test. A networkError
  //     carrying a structured GraphQL body comes back false here on purpose and
  //     must fall through to the branches below.
  if (isNetwork) return { field: 'form', message: NETWORK_MESSAGE };

  // `apollo.ts`'s errorLink already clears the session and redirects on this
  // one. A page that also rendered it would race that redirect with a banner
  // the user has no time to read.
  if (m.includes('authentication must be provided')) {
    return { field: 'form', message: '', passthrough: true };
  }

  // 2 ─ Google. Checked in EVERY context, not just 'google': the linked-account
  //     and blocked-account strings are thrown on the password path too. The
  //     backend wrote this copy to be user-facing, so it passes through as-is.
  if (
    m.includes('google account email is not verified') ||
    m.includes('already linked to a different google account') ||
    m.includes('more than one promostork account') ||
    m.includes('this account has been blocked') ||
    m.includes('this account signs in with google') ||
    m.includes('re-authenticate with google')
  ) {
    return { field: 'form', message };
  }

  // 3 ─ Reset token. The backend throws exactly 'Token Not Found' and 'Invalid
  //     Token'; both mean the same thing to a user — this link is dead.
  if (m.includes('token not found') || m.includes('invalid token')) {
    return { field: 'form', message: TOKEN_MESSAGE };
  }

  // 4 ─ Throttle.
  if (m.includes('too many') || errorCode(err) === 'RATE_LIMITED') {
    return { field: 'form', message: THROTTLE_MESSAGE };
  }

  // Anti-enumeration, web-only: even if an older backend is still throwing the
  // 404, this page shows the same neutral card it shows on success (D-5). The
  // client is then correct whichever side deploys first.
  if (context === 'forgot' && m.includes('email not registered')) {
    return { field: 'form', message: '', suppressed: true };
  }

  // 5 ─ Generic substring branches, LAST.

  // Uniqueness first: 'username already exists' also contains 'username'.
  if (m.includes('username already exists')) {
    return { field: 'userName', message: 'That username is already taken.' };
  }
  if (m.includes('email is already registered')) {
    return { field: 'email', message: 'An account with this email already exists.' };
  }

  // Credentials collapse to one message so the form cannot be used to discover
  // which usernames exist.
  if (context === 'login' && (m.includes('user not found') || m.includes('password is invalid'))) {
    return { field: 'form', message: 'Incorrect username or password.' };
  }

  if (m.includes('should be alphanumeric') || m.includes('username can only contain')) {
    return {
      field: 'userName',
      message: 'Username can only contain letters, numbers, and underscores.',
    };
  }
  if (m.includes('username')) {
    return { field: 'userName', message: 'Please enter a valid username.' };
  }
  if (m.includes('fullname')) {
    return { field: 'fullName', message: 'Please enter your full name.' };
  }
  if (m.includes('invalid email') || m.includes('email is required')) {
    return { field: 'email', message: 'Please enter a valid email address.' };
  }
  if (m.includes('password')) {
    // On register and reset the server's password message IS the policy text
    // and is already user-facing — surface it verbatim under the field. On
    // login, stay vague: naming the problem there leaks account state.
    if (context === 'register' || context === 'reset') {
      return { field: 'password', message };
    }
    if (context === 'login') {
      return { field: 'form', message: 'Incorrect username or password.' };
    }
  }

  return { field: 'form', message: FALLBACK[context] };
}
